import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { apiAssistantChat, type AssistantChatMessage } from "../api";

const STARTER_PROMPTS = [
  "How much did I spend last month?",
  "What were my biggest expense categories this month?",
  "Show my account balances.",
];

export function AssistantPage() {
  const [messages, setMessages] = useState<AssistantChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const nextMessages: AssistantChatMessage[] = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setInput("");
    setError(null);
    setLoading(true);

    try {
      const res = await apiAssistantChat(nextMessages);
      setMessages((prev) => [...prev, { role: "assistant", content: res.message }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void sendMessage(input);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  }

  return (
    <main className="layout assistant-page">
      <section className="card assistant-card">
        <div className="assistant-header">
          <h1 className="mt-0 mb-0">Assistant</h1>
          <p className="muted small mb-0">
            Ask about your spending, categories, accounts, and transactions.
          </p>
        </div>

        <div ref={listRef} className="assistant-messages" aria-live="polite">
          {messages.length === 0 ? (
            <div className="assistant-empty">
              <p className="muted mb-0">Try one of these:</p>
              <div className="assistant-starters">
                {STARTER_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    className="btn-ghost assistant-starter"
                    disabled={loading}
                    onClick={() => void sendMessage(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div
                key={`${msg.role}-${i}`}
                className={`assistant-message assistant-message-${msg.role}`}
              >
                <p className="assistant-message-label">{msg.role === "user" ? "You" : "Assistant"}</p>
                <div className="assistant-message-body">{msg.content}</div>
              </div>
            ))
          )}
          {loading ? (
            <div className="assistant-message assistant-message-assistant">
              <p className="assistant-message-label">Assistant</p>
              <div className="assistant-message-body muted">Thinking…</div>
            </div>
          ) : null}
        </div>

        {error ? <p className="assistant-error">{error}</p> : null}

        <form className="assistant-form" onSubmit={onSubmit}>
          <textarea
            ref={inputRef}
            className="assistant-input"
            rows={2}
            value={input}
            placeholder="Ask about your budget…"
            disabled={loading}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <button type="submit" disabled={loading || !input.trim()}>
            Send
          </button>
        </form>
      </section>
    </main>
  );
}
